<template>
  <div class="px-2 sm:px-5 py-2 shadow-lg text-center z-0 bg-white">
    <div role="row">
      <h3 class="block fluid-base center break-words hyphens-auto line-clamp-3 h-32" role="columnheader">
        {{ product.title }}
      </h3>
    </div>
    <FixedAspect v-if="variants.length" :ratio="0.55" role="row">
      <div role="cell" :aria-labelledby="attributeLabelIds[0]">
        <ProductCardVariantImg
          v-if="selectedVariant"
          :product-variant="selectedVariant"
          :product-route="productRoute"
          :alt="`${product.title} - ${selectedVariant.title}`"
        />
      </div>
    </FixedAspect>
    <div
      :class="['text-xs md:text-sm break-words hyphens-auto border-b', !showVariants ? 'last-of-type:border-b-0' : '']"
      v-for="(attribute, idx) in attributeList"
      :key="idx"
      role="row"
    >
      <div role="cell" :aria-labelledby="attributeLabelIds[idx + 1]" v-html="attribute.html"></div>
    </div>
    <div v-if="showVariants" class="product-variants w-full -mt-2" role="row">
      <div role="cell" :aria-labelledby="attributeLabelIds[attributeLabelIds.length - 1]">
        <FormSelectInput
          v-if="variants.length"
          :model-value="selectedVariant?.id"
          :options="variantOptions"
          :label="product.options[0]?.name || 'Variant'"
          :show-label="false"
          appearance="light"
          text-center
          :aria-label="`${$t('compare.selectVariant')} - ${product.title}`"
          @update:modelValue="onVariantChange"
        />
      </div>
    </div>
    <div v-if="showProductLink || showAddToCart || showProductPrice" role="row">
      <div role="cell" class="gap-2 flex flex-col justify-center overflow-hidden">
        <ProductPriceLine
          v-if="showProductPrice"
          :price="selectedVariant.price"
          :compare-price="selectedVariant.compareAtPrice"
          :unit-price="selectedVariant.unitPrice"
          :unit-price-measurement="selectedVariant.unitPriceMeasurement"
          :show-quantity="variants.length === 1"
          size="xs"
          :aria-label="`${$t('account.details.price')}: ${selectedVariant.price}`"
        />
        <CtaButton
          v-if="showAddToCart"
          :disabled="!availableForSale"
          @click.native="onClickAddToCart"
          :loading="isAddingToCart"
          :aria-label="`${$t('cart.addToCart')} - ${product.title}`"
          class="min-w-0"
        >
          <CartPlusIcon class="w-4 inline-block sm:hidden" aria-hidden="true" />
          <span class="hidden sm:inline-block">{{ $t('cart.addToCart') }}</span>
        </CtaButton>
        <NuxtLink
          v-if="showProductLink"
          :to="productRoute"
          :class="[showAddToCart ? 'underline text-sm mb-2' : 'underline sm:no-underline sm:btn sm:btn-pill-green']"
          :aria-label="`${$t('shop.viewProduct')} - ${product.title}`"
        >
          {{ $t('shop.viewProduct') }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script>
import FixedAspect from '@/components/common/FixedAspect';
import ProductCardVariantImg from '@/components/shop/products/ProductCardVariantImage';
import FormSelectInput from '@/components/account/FormSelectInput';
import gql from 'graphql-tag';
import VariantFragment from '@/graphql/shopify/product/VariantFragment.gql';
import productComparisonFragment from '@/graphql/shopify/product/productComparisonFragment.gql';
import productOptionSwatchesFragment from '@/graphql/shopify/product/productOptionSwatchesFragment.gql';
import CtaButton from '@/components/common/CtaButton';
import { addVariantToCart } from '@/utils/product';
import ProductPriceLine from '@/components/shop/products/ProductPriceLine.vue';
import CartPlusIcon from '@/components/icons/CartPlusIcon.vue';
import * as Sentry from '@sentry/vue';

export const ComparisonCardProductFragment = gql`
  fragment ComparisonCardProductFragment on Product {
    id
    handle
    availableForSale
    productType
    title

    options {
      name
      values
    }

    images(first: 1) {
      edges {
        node {
          id
          src: originalSrc
          altText
          width
          height
        }
      }
    }
    variants(first: 20) {
      edges {
        node {
          ...VariantFragment
        }
      }
    }

    ...productComparisonFragment
    ...productOptionSwatchesFragment
  }
  ${VariantFragment}
  ${productComparisonFragment}
  ${productOptionSwatchesFragment}
`;

export default defineNuxtComponent({
  name: 'ProductCompareCardLarge',

  components: {
    CartPlusIcon,
    CtaButton,
    FixedAspect,
    ProductCardVariantImg,
    FormSelectInput,
    ProductPriceLine,
  },

  props: {
    product: {
      type: Object,
      default: () => null,
      required: true,
    },
    compareAttributes: {
      type: Array,
      default: () => [],
    },
    rowHeights: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      default: 0,
    },
    showVariants: {
      type: Boolean,
      default: false,
    },
    showProductLink: {
      type: Boolean,
      default: false,
    },
    showAddToCart: {
      type: Boolean,
      default: false,
    },
    showProductPrice: {
      type: Boolean,
      default: false,
    },
    attributeLabelIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      selectedVariant: null,
      isAddingToCart: false,
    };
  },

  computed: {
    productRoute() {
      return { path: this.$localeRootPath('/products/' + this.product?.handle) };
    },
    variants() {
      return this.product?.variants?.edges?.map((e) => e.node) || [];
    },
    attributes() {
      return this.product?.comparison;
    },
    optionSwatches() {
      return this.product?.optionSwatches;
    },
    attributeList() {
      return this.compareAttributes?.map((attr, idx) => {
        return { ...attr, html: this.displayAttribute(attr), height: this.rowHeights[idx] + 'px' };
      });
    },
    availableForSale() {
      return this.product.availableForSale && this.selectedVariant?.available;
    },
    variantOptions() {
      return this.variants.map((variant) => ({
        value: variant.id,
        label: variant.title,
        disabled: !variant.available,
      }));
    },
  },

  created() {
    this.selectedVariant = this.variants[0];
  },

  methods: {
    setOption(newVariant) {
      this.selectedVariant = newVariant;
    },

    onVariantChange(variantId) {
      const variant = this.variants.find((v) => v.id === variantId);
      if (variant) {
        this.selectedVariant = variant;
      }
    },

    displayAttribute(attribute) {
      const value = this.product.comparison[attribute.key] || '-';
      return Array.isArray(value) ? (value.length ? value.join(', ') : '-') : value;
    },

    displayNoSwatch(swatches) {
      return swatches && Object.keys(swatches).length < 2;
    },

    async onClickAddToCart() {
      try {
        this.isAddingToCart = true;

        await addVariantToCart.call(
          this,
          this.product,
          this.selectedVariant,
          this.isCourse,
          this.rating?.value,
          this.addOn
        );
      } catch (error) {
        Sentry.captureException(e);
      } finally {
        this.isAddingToCart = false;
      }
    },
  },
});
</script>
